/**
 * IL TAB «SFIDE» DI AMICI, la parte pura (T2-3, lotto B2): cosa dice una riga di una sfida INVIATA, e con
 * quale frase si annulla. Niente rete, niente orologio: lo stato lo dice il SERVER (`state` e' gia' quello
 * EFFETTIVO, `filtroDiStato`/`statoEffettivo` in `core/sfide.js` @4348dc6), come per la barra
 * (`azionePersistente.ts:19`).
 */
import type {
  LimuloChallengeIdentity,
  LimuloChallengeState,
  LimuloChallengeWithIdentities,
} from '../../types/limulo.ts';
import type { LimuloChallengeReason } from './errors.ts';
import type { EsitoRifiutoSfida } from './challenge.ts';

/**
 * Gli argomenti della lettura delle sfide INVIATE: le ultime 20, di OGNI stato.
 *
 * ⚠️ Nessun filtro `state`, e non e' pigrizia: il motore ne accetta piu' d'uno (`stati` puo' essere una
 * lista, `elencaSfide` in `core/sfide.js` @4348dc6), ma la rotta passa `req.query` tale e quale e RTK
 * serializza un array come `a,b` — che il motore rifiuta (`sfida.campo-illeggibile`, 400: una stringa
 * `pending,accepted` non e' uno stato). [C: letto nel codice, non provato sul vivo.] Servirebbero due
 * GET; senza filtro ne basta una, e il server dice lo stato effettivo di ciascuna.
 */
export const SFIDE_INVIATE = { direction: 'out', limit: 20 } as const;

/** Il nome di una persona come lo mostra una riga: il nickname, poi il nome, mai un id. `null` se non c'e'. */
export function nomeDaIdentita(id: LimuloChallengeIdentity | null): string | null {
  if (id === null) return null;
  const nick = id.nickname?.trim() ?? '';
  if (nick !== '') return nick;
  const nome = id.name.trim();
  return nome === '' ? null : nome;
}

/**
 * «L'altro» di una sfida INVIATA: chi ha accettato se qualcuno ha accettato (`acceptedBy`), altrimenti il
 * destinatario nominato (`target`). Le identita' vengono dal BFF (`identities`), mai da un id.
 */
export function nomeDellAltro(c: LimuloChallengeWithIdentities): string | null {
  if (c.state === 'accepted') {
    return nomeDaIdentita(c.identities.acceptedBy) ?? nomeDaIdentita(c.identities.target);
  }
  return nomeDaIdentita(c.identities.target);
}

/**
 * A che punto e' una sfida ACCETTATA, dai campi del server:
 *  · `pronta`: il torneo esiste (`started`, che il motore scrive DOPO averlo creato) e c'e' il suo id ⇒
 *    «Vai al torneo»;
 *  · `non-partita`: il motore ha esaurito i tentativi di avvio (`startAbandoned`) ⇒ nessun ingresso, e lo si dice;
 *  · `avvio`: accettata ma il torneo non c'e' ancora (il giro delle code, ogni 4 s) ⇒ nessun ingresso, per ora.
 * ⚠️ `tournamentId` da solo NON basta: il motore lo assegna all'accettazione, prima che il torneo esista, e
 * la scheda di un torneo che non c'e' ancora e' un 404.
 */
export type FaseAccettata = 'pronta' | 'non-partita' | 'avvio';
export function faseDellaAccettata(c: LimuloChallengeWithIdentities): FaseAccettata {
  if (c.started && c.tournamentId !== null && c.tournamentId !== '') return 'pronta';
  if (c.startAbandoned) return 'non-partita';
  return 'avvio';
}

/** Gli stati di una sfida inviata che hanno UN'AZIONE: solo `pending` si annulla. Gli altri sono un fatto. */
export const staAnnullabile = (state: LimuloChallengeState): boolean => state === 'pending';

/**
 * Le frasi dell'ANNULLAMENTO. Ne' quelle dell'invio ne' quelle dell'accettazione (`CHALLENGE_*_REASON_COPY`):
 * a chi annulla, «per inviare» e «per accettare» sarebbero falsi. Il motore risponde a `revoke`:
 *  · `sfida.scaduta` — e' scaduta fra due giri;
 *  · `sfida.non-in-attesa` — l'altro ha GIA' risposto (accettato o rifiutato) o e' gia' annullata;
 *  · `sfida.non-trovata` — non c'e' piu';
 *  · `sfida.solo-chi-sfida` — non e' tua: dalla UI non e' raggiungibile, e mostra il codice in chiaro.
 */
export const CHALLENGE_REVOKE_REASON_COPY: Partial<Record<LimuloChallengeReason, string>> = {
  'sfida.scaduta': 'social.sfide_annulla_scaduta',
  'sfida.non-in-attesa': 'social.sfide_annulla_non_in_attesa',
  'sfida.non-trovata': 'social.sfide_annulla_non_trovata',
};

/** Come `rifiutoSfidaOf`/`rifiutoAccettaSfidaOf`, con le frasi dell'annullamento. Stesso tipo di esito. */
export function rifiutoRevocaSfidaOf(reason: LimuloChallengeReason | null, codiceGrezzo: string): EsitoRifiutoSfida {
  if (reason !== null) {
    const chiave = CHALLENGE_REVOKE_REASON_COPY[reason];
    if (chiave !== undefined) return { kind: 'nota', motivo: reason, chiave };
    return { kind: 'generico', codice: reason };
  }
  return { kind: 'generico', codice: codiceGrezzo };
}
